import { WorkSpaceNode } from 'stores/WorkSpaceStoreAdapter';

/** ツリー描画用のノード（子を持つ） */
export type TreeNodeData = WorkSpaceNode & {
  children: TreeNodeData[];
};

/**
 * フラットな WorkSpaceNode 配列をツリー構造に変換する。
 * 各階層は「フォルダ先 → ファイル後、名前昇順」でソートする。
 */
export const buildTree = (nodes: WorkSpaceNode[]): TreeNodeData[] => {
  const map = new Map<string, TreeNodeData>();
  nodes.forEach((n) => map.set(n.id, { ...n, children: [] }));

  const roots: TreeNodeData[] = [];
  map.forEach((node) => {
    if (node.parentId && map.has(node.parentId)) {
      map.get(node.parentId)!.children.push(node);
    } else {
      roots.push(node);
    }
  });

  const sortNodes = (list: TreeNodeData[]) => {
    list.sort((a, b) => {
      if (a.type !== b.type) {
        return a.type === 'FOLDER' ? -1 : 1;
      }
      return a.name.localeCompare(b.name, 'ja');
    });
    list.forEach((n) => sortNodes(n.children));
  };
  sortNodes(roots);

  return roots;
};

/** ツリー内の全フォルダ id を収集する（全て開く用） */
export const collectFolderIds = (nodes: WorkSpaceNode[]): string[] =>
  nodes.filter((n) => n.type === 'FOLDER').map((n) => n.id);

/**
 * candidateId が ancestorId の子孫（自分自身を含む）かどうかを判定する。
 * D&D 移動で「フォルダを自分自身/子孫の配下へドロップ」を抑止するのに使う。
 */
export const isSelfOrDescendant = (
  ancestorId: string,
  candidateId: string,
  nodes: WorkSpaceNode[],
): boolean => {
  if (ancestorId === candidateId) return true;
  const byId = new Map(nodes.map((n) => [n.id, n]));
  let current = byId.get(candidateId);
  const visited = new Set<string>();
  while (current && current.parentId && !visited.has(current.parentId)) {
    if (current.parentId === ancestorId) return true;
    visited.add(current.parentId);
    current = byId.get(current.parentId);
  }
  return false;
};

/**
 * ファイル名から拡張子（小文字）を切り出す。拡張子がなければ null。
 */
export const extractExtension = (name: string): string | null => {
  const trimmed = name.trim();
  const idx = trimmed.lastIndexOf('.');
  if (idx <= 0 || idx === trimmed.length - 1) {
    return null;
  }
  return trimmed.slice(idx + 1).toLowerCase();
};

/**
 * 新規作成の起点となる親フォルダ id を求める。
 * - 選択中がフォルダ: そのフォルダ
 * - 選択中がファイル: その親フォルダ
 * - 未選択: ルート（null）
 */
export const resolveCreateParentId = (
  selectedId: string | null,
  nodes: WorkSpaceNode[],
): string | null => {
  if (!selectedId) return null;
  const selected = nodes.find((n) => n.id === selectedId);
  if (!selected) return null;
  if (selected.type === 'FOLDER') return selected.id;
  return selected.parentId ?? null;
};

/** 親 id から表示用のパス文字列を組み立てる（例: /src/main） */
export const buildPathLabel = (parentId: string | null, nodes: WorkSpaceNode[]): string => {
  if (!parentId) return '/';
  const map = new Map(nodes.map((n) => [n.id, n]));
  const segments: string[] = [];
  let current = map.get(parentId);
  while (current) {
    segments.unshift(current.name);
    current = current.parentId ? map.get(current.parentId) : undefined;
  }
  return '/' + segments.join('/');
};
